import {CardElement, Elements, ElementsConsumer, PaymentRequestButtonElement} from '@stripe/react-stripe-js';
import {
  CanMakePaymentResult,
  PaymentMethod,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  Stripe,
  StripeElements,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import {useCallback, useEffect, useRef, useState} from 'react';
import checkout_logos from './assets/checkout-logos.webp';
import mastercard_logo from './assets/mastercard_id_check.png';
import purchase_lock from './assets/purchase_lock.svg';
import ssl_logo from './assets/secure_ssl_encryption.png';
import stripe_logo from './assets/stripe.png';
import visa_logo from './assets/visa_secure.png';
import {createStripeClinicInstance} from 'externalAPIs/stripe';
import Analytics from 'utils/Analytics';
import {PlanInfo} from 'utils/stripePlans';
import styles from './ClinicPaymentElement.module.css';
import {createClinicCardHold, payClinicWithCreditCard} from 'externalAPIs/hypnos';
import {getEmailFromSurveyData} from 'utils/getEmailFromSurveyData';
import {getValueFromSurveyData} from 'utils/getValueFromSurveyData';

const GENERIC_CHECKOUT_ERROR = 'Could not complete checkout. Please try again or use an alternate method.';
// No hypnos session (missing or rejected token). There's no way to mint a new one from the
// browser, so point the patient at the care team rather than leaving the form dead.
export const SESSION_LOST_ERROR =
  "We couldn't load secure checkout for your account. Please contact care@risescience.com and we'll help you finish booking.";
export const CARD_HOLD_FAILED_ERROR = "We couldn't load secure checkout. Please refresh the page to try again.";

export const ClinicPaymentElement = ({
  onSuccess,
  planToPurchase,
  onLoadingChanged,
  variant,
}: {
  variant?: 'default' | 'purple';
  onSuccess: (subscription: string) => void;
  planToPurchase: PlanInfo;
  onLoadingChanged: (loading: boolean) => void;
}) => {
  const [error, setError] = useState<string>('');
  // Set when the card hold can't be created, so the patient sees why checkout won't work
  const [setupError, setSetupError] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const [clientSecret, setClientSecret] = useState<string | undefined>('');
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [options, setOptions] = useState<StripeElementsOptions | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [canMakePayment, setCanMakePayment] = useState<CanMakePaymentResult | null>(null);

  const loadingStripeRef = useRef<boolean>(false);

  const handlePurchase = useCallback(
    async (purchaseMethod: () => Promise<{subscription: string | null; error: string | null}>) => {
      setError('');
      onLoadingChanged(true);
      const {subscription, error} = await purchaseMethod();
      if (subscription) {
        onLoadingChanged(false);
        onSuccess(subscription);
      } else {
        onLoadingChanged(false);
        setError(error ?? 'Could not complete checkout. Please try again or use an alternate method.');
      }
    },
    [onSuccess, setError, onLoadingChanged],
  );

  const purchaseWithCreditCard = async (elements: StripeElements | null) => {
    if (!termsAccepted) {
      setError('Please accept the Terms, Consents, and Conditions to continue.');
      return;
    }

    // Gather the customer's billing information (for example, email)
    const email = getEmailFromSurveyData();
    const firstName = getValueFromSurveyData('first_name') || '';
    const lastName = getValueFromSurveyData('last_name') || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const phone = getValueFromSurveyData('phone') || '';

    const billingDetails = {
      email,
      phone,
      name: fullName,
    };

    if (!clientSecret) {
      setError(setupError || GENERIC_CHECKOUT_ERROR);
      return;
    }

    const cardElement = elements?.getElement(CardElement) ?? null;

    await handlePurchase(
      async () => await payClinicWithCreditCard(billingDetails, cardElement, null, stripeInstance, clientSecret),
    );
  };

  // Apple Pay Handler: If user can use Apple/Google Pay, set up the payment request handler
  useEffect(() => {
    const handler = async (event: PaymentRequestPaymentMethodEvent) => {
      if (!termsAccepted) {
        event.complete('fail');
        setError('Please accept the Terms, Consents, and Conditions to continue.');
        return {
          subscription: null,
          error: 'Please accept the Terms, Consents, and Conditions to continue.',
        };
      }

      if (!clientSecret) {
        const message = setupError || GENERIC_CHECKOUT_ERROR;
        event.complete('fail');
        setError(message);
        return {subscription: null, error: message};
      }

      await handlePurchase(
        async () =>
          await payClinicWithCreditCard(null, null, event.paymentMethod as PaymentMethod, stripeInstance, clientSecret),
      );
    };

    // Listen for event to handle Apple/Google Pay
    if (paymentRequest) {
      paymentRequest.on('paymentmethod', handler);
    }

    return () => {
      // Unsubscribe from the event when the useEffect is re-rendered
      if (paymentRequest) {
        paymentRequest.off('paymentmethod', handler);
      }
    };
  }, [planToPurchase, paymentRequest, handlePurchase, clientSecret, stripeInstance, setupError, termsAccepted]);

  useEffect(() => {
    const setupStripe = async () => {
      if (!stripeInstance && !loadingStripeRef.current) {
        loadingStripeRef.current = true;

        // Get user info from survey data
        const email = getEmailFromSurveyData();
        const firstName = getValueFromSurveyData('first_name') || '';
        const lastName = getValueFromSurveyData('last_name') || '';
        const person_id = getValueFromSurveyData('person_id') || null;
        const fullName = `${firstName} ${lastName}`.trim();
        // Hypnos session JWTs have no `exp`, so a stored token is used as-is; the server is the
        // authority on whether it's still valid (a rejection surfaces as authError).
        const token = getValueFromSurveyData('hypnos_token') || '';

        let client_secret: string | undefined;
        if (!token || !person_id) {
          Analytics.track('Insomnia Clinic Checkout Missing Session', {hasToken: !!token, hasPersonId: !!person_id});
          setSetupError(SESSION_LOST_ERROR);
        } else {
          try {
            const result = await createClinicCardHold(email, fullName, person_id, token);
            client_secret = result.client_secret;
            if (!client_secret) {
              setSetupError(result.authError ? SESSION_LOST_ERROR : CARD_HOLD_FAILED_ERROR);
            }
          } catch (error) {
            setSetupError(CARD_HOLD_FAILED_ERROR);
          }
        }

        setClientSecret(client_secret);
        setOptions({
          clientSecret: client_secret,
          appearance: {
            variables: {
              colorPrimary: '#0570de',
              colorBackground: '#30313d',
              colorText: 'white',
              colorDanger: '#df1b41',
              borderRadius: '4px',
              colorPrimaryText: 'white',
              fontSizeSm: '1rem',
            },
          },
        });
        const stripe = await createStripeClinicInstance();
        if (stripe) {
          setStripeInstance(stripe);
        }
        loadingStripeRef.current = false;
      }
    };

    setupStripe();
  }, [stripeInstance, loadingStripeRef]);

  useEffect(() => {
    if (!stripeInstance) return;

    const updatePaymentRequest = () => {
      if (paymentRequest) {
        paymentRequest.update({
          total: {
            label: planToPurchase.frequency,
            amount: planToPurchase.purchasePrice,
            pending: false,
          },
        });
        paymentRequest.canMakePayment().then(result => {
          setCanMakePayment(result);
        });
      }
    };

    const createPaymentRequest = () => {
      const newPaymentRequest = stripeInstance.paymentRequest({
        country: 'US',
        currency: planToPurchase.currency,
        total: {label: planToPurchase.frequency, amount: planToPurchase.purchasePrice, pending: false},
        requestPayerEmail: true,
        requestPayerName: true,
        requestPayerPhone: true,
      });

      newPaymentRequest.canMakePayment().then(result => {
        if (result) {
          setPaymentRequest(newPaymentRequest);
          setCanMakePayment(result);
          const {applePay, googlePay} = result;
          Analytics.track('Platform Pay Options Shown', {applePay, googlePay, ...planToPurchase});
        } else {
          setPaymentRequest(undefined);
          setCanMakePayment(null);
          Analytics.track('Platform Pay Options Shown', {applePay: false, googlePay: false, ...planToPurchase});
        }
      });
    };

    if (paymentRequest) {
      updatePaymentRequest();
    } else {
      createPaymentRequest();
    }
  }, [planToPurchase, paymentRequest, stripeInstance]);

  if (!stripeInstance || !options) {
    return null;
  }

  const visibleError = error || setupError;

  return (
    <Elements stripe={stripeInstance} options={options}>
      <ElementsConsumer>
        {({elements}) => (
          <section>
            {visibleError ? <p style={{color: 'red'}}>{visibleError}</p> : null}
            <div className={styles.checkoutContainer}>
              {stripeInstance && paymentRequest && (canMakePayment?.applePay || canMakePayment?.googlePay) ? (
                <div className={styles.mt4}>
                  <div style={{opacity: termsAccepted ? 1 : 0.5, pointerEvents: termsAccepted ? 'auto' : 'none'}}>
                    <PaymentRequestButtonElement
                      options={{
                        style: {
                          paymentRequestButton: {
                            theme: 'dark',
                            height: '55px',
                          },
                        },
                        paymentRequest,
                      }}
                      onClick={() => {
                        Analytics.track('Platform Pay Clicked');
                      }}
                    />
                  </div>
                  <div className={styles.divider}>
                    <span>OR</span>
                  </div>
                </div>
              ) : null}

              <div className={styles.terms}>
                <label className={styles.termsCheckboxLabel}>
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    className={styles.termsCheckbox}
                  />
                  <span>
                    By continuing, I confirm that I have read and agreed to all{' '}
                    <a
                      href="https://www.risescience.com/rise-health-tos-privacy"
                      className={styles.termsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms, Consents, and Conditions
                    </a>
                  </span>
                </label>
              </div>

              <div className={styles.creditCard}>
                <div className={styles.cardOptions}>
                  <div className={styles.creditCardTitle}>Credit card</div>
                  <div style={{height: '1.5rem'}}>
                    <img width="150" className={styles.paymentLogo} src={checkout_logos} alt="Card logos" />
                  </div>
                </div>
                <p className={styles.cardDetails}>Card details</p>
                <form
                  onSubmit={async event => {
                    event.preventDefault();
                    await purchaseWithCreditCard(elements);
                  }}
                >
                  <div
                    style={{
                      border: '1px solid #D3D3D3',
                      borderRadius: '2px',
                      padding: '1.5px',
                      paddingLeft: '12px',
                      marginBottom: '1rem',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      backgroundColor: 'white',
                    }}
                  >
                    <CardElement
                      options={{
                        style: {
                          base: {
                            backgroundColor: 'white',
                          },
                        },
                      }}
                      onFocus={() => {
                        Analytics.track('Credit Card Form Focused');
                      }}
                    />
                  </div>

                  <button
                    id="submit"
                    className={variant === 'purple' ? styles.purpleButton : styles.blackButton}
                    disabled={!termsAccepted}
                  >
                    <div style={{display: 'flex', justifyContent: 'center'}}>
                      <div className={styles.confirmPurchaseButton}>
                        <img src={purchase_lock} alt=" Purchase Lock" />
                        <span>Confirm Purchase</span>
                      </div>
                    </div>
                  </button>
                </form>
                <div className={styles.encryption}>
                  <div className={styles.encryptionDetails}>
                    Your payment information is secure with SSL/TLS encryption
                  </div>
                  <div className={styles.paymentSecurityImageContainer}>
                    <div className={styles.paymentSecurityImage}>
                      <img className={styles.paymentLogo} src={ssl_logo} alt="Secure SSL Encryption" />
                    </div>
                    <div className={styles.paymentSecurityImage}>
                      <img className={styles.paymentLogo} src={visa_logo} alt="Visa Secure" />
                    </div>
                    <div className={styles.paymentSecurityImage}>
                      <img className={styles.paymentLogo} src={stripe_logo} alt="Powered By Stripe" />
                    </div>
                    <div className={styles.paymentSecurityImage}>
                      <img className={styles.paymentLogo} src={mastercard_logo} alt="Mastercard ID Check" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </ElementsConsumer>
    </Elements>
  );
};
